import React from 'react';
import PropTypes from 'prop-types';
import { Box, Button as Buttons, Divider, StackView, TextInput, Typography } from '@telus-uds/components-web';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import ReactSelect from 'react-select';
import { useLocaleContext } from '../../../context/LocaleContext';
import { deviceQuestions } from './constants';
import ErrorMessage from '../../../errorMessage';
import {
  FormContainer,
  FieldOptionsGroup
} from './css';

const DeviceForm = ({ 
  editingId, 
  formValues, 
  onSubmit, 
  onReset, 
  onCancelEdit, 
  DEVICE_MATRIX 
}) => {
  const locale = useLocaleContext();

  // Create validation schema based on locale
  const getValidationSchema = () => {
    const fieldRequiredMsg = (DEVICE_MATRIX.VALIDATION && DEVICE_MATRIX.VALIDATION.FIELD_REQUIRED) || 
                             (locale === 'en-US' ? 'This field is Required' : 'Ce champ est requis');
    
    const numberInvalidMsg = (DEVICE_MATRIX.VALIDATION && DEVICE_MATRIX.VALIDATION.NUMBER_INVALID) || 
                             (locale === 'en-US' ? 'Must be a number value' : 'Doit être une valeur numérique');
    
    const positiveNumberMsg = (DEVICE_MATRIX.VALIDATION && DEVICE_MATRIX.VALIDATION.POSITIVE_NUMBER) || 
                              (locale === 'en-US' ? 'Must be zero or greater' : 'Doit être zéro ou supérieur');

    // Build validation schema for answers (device configuration)
    const answersSchema = {};
    deviceQuestions.forEach(question => {
      if (question.key === 'customerSegment') {
        // Segment field is conditionally required - only when C2F is NOT "Yes" or "Oui"
        answersSchema[question.key] = Yup.array()
          .when('coperToFibreMigration', {
            is: (c2fValue) => {
              // If C2F is not set or empty, require Segment
              if (!c2fValue || c2fValue.length === 0) return true;
              
              // Check if C2F has only "Yes" or "Oui" (no "No" or "Non")
              const hasYes = c2fValue.some(option => option.en === 'Yes' || option.fr === 'Oui');
              const hasNo = c2fValue.some(option => option.en === 'No' || option.fr === 'Non');
              
              // Only require Segment if C2F is not exclusively "Yes"
              return !(hasYes && !hasNo);
            },
            then: (schema) => schema.min(1, fieldRequiredMsg).required(fieldRequiredMsg),
            otherwise: (schema) => schema.notRequired()
          });
      } else {
        // All other fields are always required
        answersSchema[question.key] = Yup.array()
          .min(1, fieldRequiredMsg)
          .required(fieldRequiredMsg);
      }
    });

    return Yup.object().shape({
      name: Yup.string()
        .required(fieldRequiredMsg),
      deviceType: Yup.string()
        .required(fieldRequiredMsg),
      familyType: Yup.string()
        .required(fieldRequiredMsg),
      deviceSku: Yup.string()
        .required(fieldRequiredMsg),
      oneTimePrice: Yup.string()
        .required(fieldRequiredMsg)
        .test('is-number', numberInvalidMsg, (value) => {
          const numericValue = parseFloat(value);
          return !isNaN(numericValue);
        })
        .test('is-positive', positiveNumberMsg, (value) => {
          const numericValue = parseFloat(value);
          return numericValue >= 0;
        }),
      easyPaymentPrice: Yup.string()
        .required(fieldRequiredMsg)
        .test('is-number', numberInvalidMsg, (value) => {
          const numericValue = parseFloat(value);
          return !isNaN(numericValue);
        })
        .test('is-positive', positiveNumberMsg, (value) => {
          const numericValue = parseFloat(value);
          return numericValue >= 0;
        }),
      answers: Yup.object().shape(answersSchema)
    });
  };

  const validationSchema = getValidationSchema();

  return (
    <FormContainer data-form-section="device-form">
      <StackView space={4}>
        <Typography heading="h3" variant={{ size: 'h3' }} block>
          {editingId 
            ? ((DEVICE_MATRIX.FORM && DEVICE_MATRIX.FORM.UPDATE_DEVICE) || 'Update Device') 
            : ((DEVICE_MATRIX.FORM && DEVICE_MATRIX.FORM.ADD_DEVICE) || 'Add Device')
          }
        </Typography>
        <Divider space={0} variant={{ inverse: true }} />
        
        <Formik
          initialValues={formValues}
          enableReinitialize
          validationSchema={validationSchema}
          onSubmit={onSubmit}
          validateOnBlur={true}
          validateOnChange={false}
          validateOnMount={false}
        >
          {({ values, setFieldValue, resetForm, errors, touched, setFieldTouched, setTouched, submitForm, isSubmitting, setSubmitting, validateForm, setErrors, setFieldError }) => {
            
            // Custom submit handler that touches all fields before validation
            const handleSubmit = async (e) => {
              e.preventDefault();
              
              // Touch all form fields to trigger validation display
              const allFields = [
                'name', 'deviceType', 'familyType', 'deviceSku',
                'oneTimePrice', 'easyPaymentPrice'
              ];
              
              const allAnswerFields = deviceQuestions.map(q => q.key);
              
              // Touch all basic fields
              allFields.forEach(field => setFieldTouched(field, true));
              
              // Touch all answer fields
              allAnswerFields.forEach(fieldKey => setFieldTouched(`answers.${fieldKey}`, true));
              
              // Force validation by touching the entire answers object
              setFieldTouched('answers', true);
              
              // Force validation using Formik's validateForm
              const validationErrors = await validateForm();
              
              // If there are validation errors, don't submit
              if (validationErrors && Object.keys(validationErrors).length > 0) {
                // Set errors for each field individually
                Object.keys(validationErrors).forEach(fieldName => {
                  if (fieldName === 'answers') {
                    // Handle answers object errors
                    Object.keys(validationErrors.answers).forEach(answerKey => {
                      setFieldError(`answers.${answerKey}`, validationErrors.answers[answerKey]);
                      setFieldTouched(`answers.${answerKey}`, true);
                    });
                  } else {
                    // Handle regular field errors
                    setFieldError(fieldName, validationErrors[fieldName]);
                    setFieldTouched(fieldName, true);
                  }
                });
                
                return;
              }
              
              // If no validation errors, proceed with submission
              submitForm();
            };
            
            // Check if C2F is set to ONLY Yes/Oui (not both Yes and No)
            const shouldDisableSegment = () => {
              const c2fValue = (values.answers && values.answers.coperToFibreMigration) || [];
              if (c2fValue.length === 0) return false;
              
              // Check if only Yes/Oui is selected (no No/Non)
              const hasYes = c2fValue.some(option => option.en === 'Yes' || option.fr === 'Oui');
              const hasNo = c2fValue.some(option => option.en === 'No' || option.fr === 'Non');
              
              // Only disable if Yes is selected AND No is not selected
              return hasYes && !hasNo;
            };

            const getBorderColor = (hasError, isFocused) => {
              if (hasError) return '#d32f2f';
              if (isFocused) return '#6b7280';
              return '#d1d5db';
            };

            const getBoxShadow = (hasError, isFocused) => {
              if (hasError) return '0 0 0 1px #d32f2f';
              if (isFocused) return '0 0 0 1px #6b7280';
              return 'none';
            };

            const getHoverBorderColor = (hasError, isSegmentField, disableSegment) => {
              if (hasError) return '#d32f2f';
              if (isSegmentField && disableSegment()) return '#d1d5db';
              return '#9ca3af';
            };

            return (
              <Form>
                <StackView space={4}>
                  <FieldOptionsGroup>
                    <div>
                      <TextInput
                        label={`${(DEVICE_MATRIX.FORM && DEVICE_MATRIX.FORM.DEVICE_NAME) || 'Device Name'} *`}
                        value={values.name || ''}
                        onChange={(value) => setFieldValue('name', value)}
                        placeholder="Enter device name..."
                        validation={touched.name && errors.name ? 'error' : undefined}
                      />
                      {touched.name && errors.name && (
                        <ErrorMessage type="text" text={errors.name} />
                      )}
                    </div>
                    
                    <div>
                      <TextInput
                        label={`${(DEVICE_MATRIX.FORM && DEVICE_MATRIX.FORM.DEVICE_TYPE) || 'Device Type'} *`}
                        value={values.deviceType || ''}
                        onChange={(value) => setFieldValue('deviceType', value)}
                        placeholder="Enter device type..."
                        validation={touched.deviceType && errors.deviceType ? 'error' : undefined}
                      />
                      {touched.deviceType && errors.deviceType && (
                        <ErrorMessage type="text" text={errors.deviceType} />
                      )}
                    </div>
                    
                    <div>
                      <TextInput
                        label={`${(DEVICE_MATRIX.FORM && DEVICE_MATRIX.FORM.FAMILY_TYPE) || 'Family Type'} *`}
                        value={values.familyType || ''}
                        onChange={(value) => setFieldValue('familyType', value)}
                        placeholder="Enter family type..."
                        validation={touched.familyType && errors.familyType ? 'error' : undefined}
                      />
                      {touched.familyType && errors.familyType && (
                        <ErrorMessage type="text" text={errors.familyType} />
                      )}
                    </div>
                    
                    <div>
                      <TextInput
                        label={`${(DEVICE_MATRIX.FORM && DEVICE_MATRIX.FORM.SKU) || 'SKU'} *`}
                        value={values.deviceSku || ''}
                        onChange={(value) => setFieldValue('deviceSku', value)}
                        placeholder="Enter device SKU..."
                        validation={touched.deviceSku && errors.deviceSku ? 'error' : undefined}
                      />
                      {touched.deviceSku && errors.deviceSku && (
                        <ErrorMessage type="text" text={errors.deviceSku} />
                      )}
                    </div>
                    
                    <div>
                      <TextInput
                        label={(DEVICE_MATRIX.FORM && DEVICE_MATRIX.FORM.ONE_TIME_PRICE) || 'One Time Price'}
                        value={values.oneTimePrice || ''}
                        onChange={(value) => setFieldValue('oneTimePrice', value)}
                        placeholder="Enter one time price..."
                        validation={touched.oneTimePrice && errors.oneTimePrice ? 'error' : undefined}
                      />
                      {touched.oneTimePrice && errors.oneTimePrice && (
                        <ErrorMessage type="text" text={errors.oneTimePrice} />
                      )}
                    </div>
                    
                    <div>
                      <TextInput
                        label={(DEVICE_MATRIX.FORM && DEVICE_MATRIX.FORM.EASY_PAYMENT_PRICE) || 'Easy Payment Price'}
                        value={values.easyPaymentPrice || ''}
                        onChange={(value) => setFieldValue('easyPaymentPrice', value)}
                        placeholder="Enter easy payment price..."
                        validation={touched.easyPaymentPrice && errors.easyPaymentPrice ? 'error' : undefined}
                      />
                      {touched.easyPaymentPrice && errors.easyPaymentPrice && (
                        <ErrorMessage type="text" text={errors.easyPaymentPrice} />
                      )}
                    </div>
                  </FieldOptionsGroup>

                  {/* Device Configuration Questions */}
                  <StackView space={3}>
                    <Typography heading="h4" variant={{ size: 'h4' }} block>
                      {(DEVICE_MATRIX.FORM && DEVICE_MATRIX.FORM.DEVICE_CONFIGURATION) || 'Device Configuration'}
                    </Typography>
                    <FieldOptionsGroup>
                      {deviceQuestions.map((question) => {
                        const isEnglish = locale === 'en-US';

                        const getOptionLabel = (option) => {
                          if (option.displayLabel) {
                            return isEnglish ? option.displayLabel.en : option.displayLabel.fr;
                          }
                          return isEnglish ? option.en : option.fr;
                        };

                        // Convert options to react-select format
                        const selectOptions = question.options.map(option => ({
                          label: getOptionLabel(option),
                          value: option.en, // Always use English as value for consistency
                          data: option // Store the full object for payload
                        }));

                        // Get current selected values
                        const selectedOptions = (values.answers[question.key] || []).map(value => {
                          if (typeof value === 'object' && value.en) {
                            const matchedOption = question.options.find(o => o.en === value.en);
                            const fallbackLabel = isEnglish ? value.en : value.fr;
                            return {
                              label: matchedOption ? getOptionLabel(matchedOption) : fallbackLabel,
                              value: value.en,
                              data: value
                            };
                          }
                          // Fallback for values not in options
                          return { 
                            label: locale === 'en-US' ? value : value, 
                            value, 
                            data: { en: value, fr: value } 
                          };
                        });

                        const isSegmentField = question.key === 'customerSegment';
                        
                        // Check for validation errors for this field
                        const fieldError = errors.answers && errors.answers[question.key];
                        const isTouchedField = touched.answers && touched.answers[question.key];
                        // Don't show error for Segment field when it's disabled
                        const hasError = fieldError && isTouchedField && !(isSegmentField && shouldDisableSegment());

                        return (
                          <div key={question.key} style={{ marginBottom: '1rem' }}>
                            <div style={{ marginBottom: '0.5rem', fontWeight: '600' }}>
                              {locale === 'en-US' ? question.label.en : question.label.fr}
                            </div>
                            <ReactSelect
                              value={selectedOptions}
                              onChange={(selectedOptionsNew) => {
                                // Convert back to our format for payload
                                const newValues = selectedOptionsNew.map(option => option.data);
                                setFieldValue(`answers.${question.key}`, newValues);
                              }}
                              options={selectOptions}
                              isMulti
                              closeMenuOnSelect={false}
                              disabled={isSegmentField && shouldDisableSegment()}
                              placeholder={`Select ${(locale === 'en-US' ? question.label.en : question.label.fr).toLowerCase()}...`}
                              theme={theme => ({
                                ...theme,
                                colors: {
                                  ...theme.colors,
                                  primary: '#6b7280',
                                  primary75: '#9ca3af',
                                  primary50: '#d1d5db',
                                  primary25: '#f3f4f6'
                                }
                              })}
                              styles={{
                                control: (provided, state) => ({
                                  ...provided,
                                  backgroundColor: (isSegmentField && shouldDisableSegment()) ? '#f5f5f5' : 'white',
                                  borderColor: getBorderColor(hasError, state.isFocused),
                                  boxShadow: getBoxShadow(hasError, state.isFocused),
                                  '&:hover': {
                                    borderColor: getHoverBorderColor(hasError, isSegmentField, shouldDisableSegment)
                                  }
                                }),
                                option: provided => ({
                                  ...provided,
                                  padding: '8px 12px',
                                  fontSize: '14px',
                                  backgroundColor: provided.isSelected ? '#f3f4f6' : 'white',
                                  color: '#374151',
                                  '&:hover': {
                                    backgroundColor: '#f3f4f6'
                                  }
                                }),
                                multiValue: provided => ({
                                  ...provided,
                                  backgroundColor: '#f5f5f5',
                                  borderRadius: '4px',
                                  border: '1px solid #e0e0e0'
                                }),
                                multiValueLabel: provided => ({
                                  ...provided,
                                  color: '#424242',
                                  fontSize: '12px'
                                }),
                                multiValueRemove: provided => ({
                                  ...provided,
                                  color: '#757575',
                                  '&:hover': {
                                    backgroundColor: '#e0e0e0',
                                    color: '#424242'
                                  }
                                }),
                                placeholder: provided => ({
                                  ...provided,
                                  color: '#9ca3af'
                                }),
                                singleValue: provided => ({
                                  ...provided,
                                  color: '#374151'
                                }),
                                menuPortal: base => ({ ...base, zIndex: 9999 })
                              }}
                              menuPortalTarget={document.body}
                            />
                            {hasError && (
                              <ErrorMessage type="text" text={fieldError} />
                            )}
                            {isSegmentField && shouldDisableSegment() && (
                              <div style={{ 
                                fontSize: '12px', 
                                color: '#666', 
                                marginTop: '4px',
                                fontStyle: 'italic'
                              }}>
                                {locale === 'en-US' 
                                  ? 'Segment is disabled when C2F is Yes' 
                                  : 'Segment est désactivé quand C2F est Oui'
                                }
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </FieldOptionsGroup>
                  </StackView>

                  <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                    <Buttons type="button" variant={{ priority: 'high' }} onPress={handleSubmit}>
                      {editingId 
                        ? ((DEVICE_MATRIX.FORM && DEVICE_MATRIX.FORM.UPDATE_DEVICE) || 'Update Device') 
                        : ((DEVICE_MATRIX.FORM && DEVICE_MATRIX.FORM.ADD_DEVICE) || 'Add Device')
                      }
                    </Buttons>
                    {editingId ? (
                      <Buttons variant={{ priority: 'low' }} onPress={onCancelEdit}>
                        {(DEVICE_MATRIX.FORM && DEVICE_MATRIX.FORM.CANCEL_EDIT) || 'Cancel Edit'}
                      </Buttons>
                    ) : (
                      <Buttons variant={{ priority: 'low' }} onPress={() => {
                        resetForm();
                        onReset();
                      }}>
                        {(DEVICE_MATRIX.FORM && DEVICE_MATRIX.FORM.RESET) || 'Reset Form'}
                      </Buttons>
                    )}
                  </div>
                </StackView>
              </Form>
            );
          }}
        </Formik>
      </StackView>
    </FormContainer>
  );
};

DeviceForm.propTypes = {
  editingId: PropTypes.number,
  formValues: PropTypes.shape({
    name: PropTypes.string,
    deviceType: PropTypes.string,
    familyType: PropTypes.string,
    deviceSku: PropTypes.string,
    oneTimePrice: PropTypes.string,
    easyPaymentPrice: PropTypes.string,
    answers: PropTypes.object
  }).isRequired,
  onSubmit: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  onCancelEdit: PropTypes.func.isRequired,
  DEVICE_MATRIX: PropTypes.shape({
    FORM: PropTypes.shape({
      UPDATE_DEVICE: PropTypes.string,
      ADD_DEVICE: PropTypes.string,
      DEVICE_NAME: PropTypes.string,
      DEVICE_TYPE: PropTypes.string,
      FAMILY_TYPE: PropTypes.string,
      SKU: PropTypes.string,
      ONE_TIME_PRICE: PropTypes.string,
      EASY_PAYMENT_PRICE: PropTypes.string,
      DEVICE_CONFIGURATION: PropTypes.string,
      CANCEL_EDIT: PropTypes.string,
      RESET: PropTypes.string
    }),
    VALIDATION: PropTypes.shape({
      FIELD_REQUIRED: PropTypes.string,
      PATTERN_INVALID: PropTypes.string,
      NUMBER_INVALID: PropTypes.string,
      POSITIVE_NUMBER: PropTypes.string
    })
  }).isRequired
};

export default DeviceForm;